import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { parseReplyDemo } from '@/lib/replyParseDemo';
import { at } from '@/data/arrivalsData';
import { t } from '@/i18n';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { stateStyles, stateLabel } from './arrivalsUi';
import { HelpTip } from '@/components/ui/help-tip';

// Typical carrier replies for a live demo, in the carrier's own language (demo content).
const SAMPLES = {
  pl: ['Będę o 15:40, korek na A4', 'Kierowca ma pauzę, może być jutro od 5:30', 'Dzisiaj nie przyjedzie, awaria ciągnika'],
  cs: ['Jsem na cestě, dorazím kolem 15:20', 'Dnes to nestihneme, zítra v 7:00', 'Auto dnes nepřijede, řidič onemocněl'],
};
const AUTO_CONFIDENCE = 0.8;

const pad = (n) => String(n).padStart(2, '0');
const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// "Simulate a carrier reply": type any reply, the AI reads it (#65). A confident reading is
// applied right away; otherwise the planner confirms it.
export const ReplySimulator = ({ loading, carrier, onApply }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(null); // { mode, result, applied, reply }

  const apply = (r, reply) => {
    const eta = r.time ? at(r.time, r.dayOffset || 0) : null;
    onApply(loading.id, {
      text: reply, textCs: r.translationCs, channel: carrier.channel,
      state: r.state, eta, power: r.state === 'no-power' ? r.time : null,
    });
  };

  const read = async () => {
    const reply = text.trim();
    if (!reply) return;
    setBusy(true);
    const now = new Date();
    const input = { text: reply, plannedAt: hhmm(new Date(loading.plannedAt)), now: hhmm(now), today: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` };
    const { mode, result } = await runAi('reply-parse', input, parseReplyDemo);
    const auto = result.state !== 'unclear' && result.confidence >= AUTO_CONFIDENCE;
    if (auto) apply(result, reply);
    setReading({ mode, result, applied: auto, reply });
    setText('');
    setBusy(false);
  };

  const r = reading?.result;
  return (
    <div className="rounded-xl border border-dashed p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="text-xs font-semibold uppercase text-muted-foreground">{t('ai.reply.title')}</div>
        <HelpTip id="reply" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(SAMPLES[carrier.lang] || SAMPLES.pl).map(s => (
          <button key={s} type="button" onClick={() => setText(s)} className="text-[11px] rounded-full border px-2 py-0.5 text-muted-foreground hover:text-foreground">
            {s}
          </button>
        ))}
      </div>
      <Textarea value={text} onChange={e => setText(e.target.value)} placeholder={t('ai.reply.placeholder')} rows={2} aria-label={t('ai.reply.title')} />
      <Button size="sm" className="gap-1" onClick={read} disabled={busy || !text.trim()}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {t('ai.reply.read')}
      </Button>

      {r && (
        <div className="rounded-lg bg-muted/50 p-3 space-y-1.5 text-sm" aria-live="polite">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={stateStyles[r.state]}>{r.state === 'unclear' ? t('ai.reply.unclear') : stateLabel(r.state)}</Badge>
            {r.time && <span className="font-semibold tabular-nums">{r.time}{r.dayOffset ? ` (${t('arrivals.tomorrow')})` : ''}</span>}
            <span className="text-xs text-muted-foreground">{t('ai.reply.confidence')} {Math.round(r.confidence * 100)} %</span>
            <AiBadge mode={reading.mode} className="ml-auto" />
          </div>
          {r.reason && <div className="text-xs">{t('ai.reply.reason')}: {r.reason}</div>}
          <div className="text-xs italic text-muted-foreground">{r.translationCs || t('ai.reply.noTranslation')}</div>
          {reading.applied ? (
            <div className="text-xs text-emerald-700 flex items-center gap-1"><Check className="h-3 w-3" /> {t('ai.reply.applied')}</div>
          ) : (
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" className="gap-1" disabled={r.state === 'unclear'}
                onClick={() => { apply(r, reading.reply); setReading({ ...reading, applied: true }); }}>
                <Check className="h-3.5 w-3.5" /> {t('ai.reply.confirm')}
              </Button>
              <Button size="sm" variant="ghost" className="gap-1" onClick={() => setReading(null)}>
                <X className="h-3.5 w-3.5" /> {t('ai.reply.dismiss')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
